import React, { useRef, useState, useContext, useEffect } from "react";
import Video from "../../UI/Video";
import Input from "../../UI/Input";
import Button from "../../UI/Button";
import Profile from "../../Sub/Profile/Profile";
import { ContextProvider, VideoChatContext } from "./Context.js";
import useForm from "../../Hooks/hook-form";
import useStopwatch from "../../Hooks/hook-stopwatch";
import { makeCall as makeCallSchema } from "../../../utils/schemas/chat.schema";
import callTypes from "../../../config/defaults/callTypes";
import "./styles.css";
import callStatus from "../../../config/defaults/callStatus";
import QuestionMarkSVG from "../../Svg/QuestionMark";
import { ImPhoneHangUp } from "react-icons/im";
import {
  BsFillMicFill,
  BsFillMicMuteFill,
  BsCameraVideoOffFill,
  BsCameraVideoFill,
} from "react-icons/bs";
import { GiSpeaker } from "react-icons/gi";
import { MdOpenInFull, MdCloseFullscreen } from "react-icons/md";
import userStatus from "../../../config/defaults/userStatus";
import {
  queryParamsExists,
  getQueryParamValue,
  removeFromURL,
} from "../../../utils/globals/URLModifier";
import { IoMdSettings } from "react-icons/io";
import { VscChromeClose } from "react-icons/vsc"

const Calling = ({
  callerId,
  callerName,
  callerAvatar,
  isCallerOffline,
  isCallerNotFound,
  isCompleted,
  isMissed,
  isNotAnswering,
  isRinging,
  isDeclined,
  onHangup,
  onContinue,
  duration,
}) => {
  const callTitle = (() => {
    if (isCallerNotFound) return "User Not Found";
    if (isCallerOffline && isCompleted) return "User went Offline";
    if (isCompleted) return "Call Ended";
    if (isRinging) return "Ringing";
    if (isDeclined) return "The User Is Busy Somewhere Else..!";
    if (isNotAnswering) return "The User Is Not Answering";
    if (isMissed) return "Not Answered";
    return "Calling";
  })();

  const callerAvatarRef = useRef();

  useEffect(() => {
    if (callerAvatarRef.current && callerAvatar) {
      callerAvatarRef.current.innerHTML = callerAvatar;
    }
  }, [callerAvatarRef.current, callerAvatar]);

  return (
    <div className="w-full h-full fixed bg-gray-800 inset-0 flex flex-col justify-center items-center z-20">
      <h2 className="text-2xl sm:text-3xl px-4 sm:px-0 text-center">
        {callTitle}
      </h2>
      <span
        ref={callerAvatarRef}
        className="avatarIncommingCall p-5 sm:p-10 rounded-full mt-20"
      >
        <QuestionMarkSVG />
      </span>
      <h2 className="text-xl sm:text-2xl mt-16">
        {callerName || callerId || "Caller Name"}
      </h2>
      {!isCallerOffline && !isMissed && !isCompleted && (
        <div className="dot-falling mt-8"></div>
      )}
      {duration && (
        <div className="mt-8 text-3xl tracking-wider">{duration}</div>
      )}
      <div className="flex flex-row justify-center items-center gap-x-2 w-3/4 sm:w-1/4 mt-4">
        {!isMissed && !isCallerOffline && !isCompleted ? (
          <Button className="bg-red-600 mt-4 py-2" onClick={onHangup}>
            Hangup
          </Button>
        ) : (
          <Button className="bg-green-700 mt-4 py-2" onClick={onContinue}>
            Continue
          </Button>
        )}
      </div>
    </div>
  );
};

const IncommingCall = ({
  incommingCallerId,
  incommingCallerName,
  incommingCallerAvatar,
  isIncommingCallerOffline,
  isMissed,
  isCompleted,
  onAccept,
  onDecline,
  onContinue,
  duration,
}) => {
  const callTitle = (() => {
    if (isMissed) return "Missed Call";
    if (isIncommingCallerOffline && isCompleted) return "Missed Call";
    if (isCompleted) {
      return "Call Ended";
    }
    return "Incomming Call";
  })();

  const incommingCallerAvatarRef = useRef();

  useEffect(() => {
    if (incommingCallerAvatarRef.current && incommingCallerAvatar) {
      incommingCallerAvatarRef.current.innerHTML = incommingCallerAvatar;
    }
  }, [incommingCallerAvatarRef.current, incommingCallerAvatar]);

  return (
    <div className="w-full h-full fixed bg-gray-800 inset-0 flex flex-col justify-center items-center z-20">
      <h2 className="text-2xl sm:text-3xl px-4 sm:px-0 text-center">
        {callTitle}
      </h2>
      <span
        ref={incommingCallerAvatarRef}
        className="avatarIncommingCall p-5 sm:p-10 rounded-full mt-20"
      >
        <QuestionMarkSVG />
      </span>
      <h2 className="text-xl sm:text-2xl mt-16">
        {incommingCallerName || incommingCallerId || "Caller Id"}
      </h2>
      {!isMissed && !isCompleted && !isIncommingCallerOffline && (
        <div className="dot-falling mt-8"></div>
      )}
      {duration && (
        <div className="mt-4 sm:mt-8 text-3xl tracking-wider">{duration}</div>
      )}
      <div className="flex flex-row justify-center items-center gap-x-2 w-3/4 sm:w-1/4 mt-4">
        {!isMissed && !isCompleted && !isIncommingCallerOffline ? (
          <>
            <Button className="bg-red-600 mt-4 py-2" onClick={onDecline}>
              Decline
            </Button>
            <Button className="bg-green-700 mt-4 py-2" onClick={onAccept}>
              Accept
            </Button>
          </>
        ) : (
          <Button className="bg-green-700 mt-4 py-2" onClick={onContinue}>
            Continue
          </Button>
        )}
      </div>
    </div>
  );
};

const CallTime = () => {
  const { seconds, minutes, hours, days } = useStopwatch({
    autoStart: true,
  });

  return (
    <>
      {days > 0 && (
        <span>
          {days}
          {" : "}
        </span>
      )}
      {hours > 0 && (
        <span>
          {hours}
          {" : "}
        </span>
      )}
      <span>
        {minutes < 10 && "0"}
        {minutes}
      </span>
      {" : "}
      <span>
        {seconds < 10 && "0"}
        {seconds}
      </span>
    </>
  );
};

const MakeCall = ({ blockedIds, onMakeCall }) => {
  const {
    register,
    handleSubmit,
    errors,
    watch,
    getValues,
    setValue,
    setError,
    clearErrors,
  } = useForm({
    schema: makeCallSchema,
  });

  const onSubmit = async (data) => {
    if (errors?.meetingId || blockedIds?.includes(data?.meetingId)) {
      setError("meetingId", { message: "You Cannot Call Yourself..!" });
      return;
    }

    onMakeCall(data?.meetingId);
  };

  const handleURLMeetingIdParse = () => {
    const meetingIdInput = getValues("meetingId");
    const hasProfileLink = meetingIdInput.match(/(https?:\/\/[^ ]*)/);
    if (
      hasProfileLink &&
      hasProfileLink.length > 0 &&
      hasProfileLink[0].includes("call=")
    ) {
      const url = (() => {
        const _temp = hasProfileLink[0].split("?");
        return _temp.length > 1 ? "?" + _temp[1] : null;
      })();

      const callerId =
        url &&
        getQueryParamValue("call", {
          customURL: url,
        });

      if (callerId) setValue("meetingId", callerId);
    }
  };

  const handleMeetingIdValidation = () => {
    const meetingIdInput = getValues("meetingId");
    if (blockedIds?.includes(meetingIdInput)) {
      setError("meetingId", { message: "You Cannot Call Yourself..!" });
    } else {
      clearErrors("meetingId");
    }
  };

  useEffect(() => {
    handleURLMeetingIdParse();
    handleMeetingIdValidation();
  }, [watch("meetingId")]);

  return (
    <div className="mt-2 w-full max-w-md m-auto bg-gray-800 rounded-xl p-7 pt-4 flex flex-col items-center">
      <form onSubmit={handleSubmit(onSubmit)} className="w-full">
        <p className="w-full text-lg">Call Someone</p>
        <Input
          placeholder="Meeting ID"
          className={`mt-3 border-2 border-gray-900 ${errors?.meetingId && `border-2 border-red-400 focus:border-red-400`
            }`}
          customProps={{ ...register("meetingId") }}
        />
        {errors?.meetingId?.message && (
          <p className="text-red-400">{errors?.meetingId?.message}</p>
        )}
        <Button className="mt-4 bg-green-700 py-2 px-5">Call</Button>
      </form>
    </div>
  );
};

const ActiveCallOptions = ({
  myStream,
  attendeeStreamRef,
  completeCall,
  updateCallMediaTrack,
  attendeeAvatar,
  activeCallWrapperRef,
}) => {
  const attendeeAvatarRef = useRef();
  const [isFullScreen, setIsFullScreen] = useState();
  const [isVideoStopped, setIsVideoStopped] = useState();
  const [isAudioStopped, setIsAudioStopped] = useState();

  useEffect(() => {
    if (attendeeAvatarRef.current && attendeeAvatar) {
      attendeeAvatarRef.current.innerHTML = attendeeAvatar;
    }
  }, [attendeeAvatarRef.current, attendeeAvatar]);

  const handleScreenToggle = () => {
    if (!isFullScreen) {
      if (activeCallWrapperRef.current.requestFullscreen) {
        activeCallWrapperRef.current.requestFullscreen();
      } else if (activeCallWrapperRef.current.webkitRequestFullscreen) {
        /* Safari */
        activeCallWrapperRef.current.webkitRequestFullscreen();
      } else if (activeCallWrapperRef.current.msRequestFullscreen) {
        /* IE11 */
        activeCallWrapperRef.current.msRequestFullscreen();
      }

      setIsFullScreen(true);
      return;
    } else if (isFullScreen) {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      } else if (document.webkitExitFullscreen) {
        /* Safari */
        document.webkitExitFullscreen();
      } else if (document.msExitFullscreen) {
        /* IE11 */
        document.msExitFullscreen();
      }

      setIsFullScreen(false);
      return;
    }
  };

  const handleVideoToggle = () => {
    if (!isVideoStopped) {
      myStream.getVideoTracks()[0].enabled = false;
      updateCallMediaTrack({
        audioEnabled: !isAudioStopped,
        videoEnabled: false,
      });
      setIsVideoStopped(true);
    } else {
      myStream.getVideoTracks()[0].enabled = true;
      updateCallMediaTrack({
        audioEnabled: !isAudioStopped,
        videoEnabled: true,
      });
      setIsVideoStopped(false);
    }
  };

  const handleAudioToggle = () => {
    if (!isAudioStopped) {
      myStream.getAudioTracks()[0].enabled = false;
      updateCallMediaTrack({
        audioEnabled: false,
        videoEnabled: !isVideoStopped,
      });
      setIsAudioStopped(true);
    } else {
      myStream.getAudioTracks()[0].enabled = true;
      updateCallMediaTrack({
        audioEnabled: true,
        videoEnabled: !isVideoStopped,
      });
      setIsAudioStopped(false);
    }
  };

  const handleVolumeChange = (e) => {
    attendeeStreamRef.current.volume = (e.target.value / 100).toFixed(1);
  };

  return (
    <>
      {/* Hover Options */}
      <div className="w-full min-h-full absolute bottom-0 z-20 hiddena flex group-hover:flex flex-col justify-between">
        {/* Top Options */}
        <div className="w-full min-h-24 sm:min-h-32 px-8 sm:px-12 md:px-24 flex justify-between items-center gap-x-5 sm:gap-x-10 absolute top-0">
          <span
            className="bg-frosted-glass px-2 py-2 sm:px-3 sm:py-3 rounded-lg custom-svg"
            ref={attendeeAvatarRef}
          ></span>
          <span className="bg-frosted-glass px-4 py-1 sm:px-8 sm:py-3 rounded-lg text-bold text-center text-lg sm:text-xl tracking-wider">
            <CallTime />
          </span>
          <Button
            className="!w-auto shadow-2xl bg-frosted-glass px-3 py-3 sm:px-5 sm:py-5 rounded-full"
            onClick={handleScreenToggle}
          >
            {!isFullScreen ? (
              <MdOpenInFull className="cursor-pointer text-xl sm:text-2xl md:text-3xl" />
            ) : (
              <MdCloseFullscreen className="cursor-pointer text-xl sm:text-2xl md:text-3xl" />
            )}
          </Button>
        </div>

        {/* Bottom Options */}
        <div className="w-full absolute bottom-0 min-h-24 sm:min-h-32 flex flex-col sm:flex-row justify-between items-center gap-x-5 gap-y-5 py-8 sm:p-y-0 sm:gap-x-10 px-8 sm:px-12 md:px-24">
          <div className="!w-auto shadow-2xl bg-frosted-glass px-3 py-3 sm:px-5 sm:py-5 rounded-full cursor-pointer items-start self-start speaker-wrapper">
            <Input
              type="range"
              min="0"
              max="100"
              onChange={handleVolumeChange}
              className="vertical-slider cursor-pointer bg-transparent outline-none w-0.5 !w-30px speaker-range"
            />
            <GiSpeaker className="cursor-pointer text-xl sm:text-2xl md:text-3xl m-auto speaker-button" />
          </div>
          <div className="flex flex-row justify-center items-center gap-x-12 self-center sm:self-end">
            <Button
              className="!w-auto shadow-2xl bg-frosted-glass px-3 py-3 sm:px-5 sm:py-5 rounded-full"
              onClick={handleAudioToggle}
            >
              {!isAudioStopped ? (
                <BsFillMicFill className="text-xl sm:text-2xl md:text-3xl" />
              ) : (
                <BsFillMicMuteFill className="text-xl sm:text-2xl md:text-3xl" />
              )}
            </Button>
            <Button
              className="!w-auto bg-red-600 px-3.5 py-3.5 sm:px-6 sm:py-6 rounded-full"
              onClick={completeCall}
            >
              <ImPhoneHangUp className="text-2xl sm:text-3xl md:text-4xl" />
            </Button>
            <Button
              className="!w-auto shadow-2xl bg-frosted-glass px-3 py-3 sm:px-5 sm:py-5 rounded-full"
              onClick={handleVideoToggle}
            >
              {!isVideoStopped ? (
                <BsCameraVideoFill className="text-xl sm:text-2xl md:text-3xl" />
              ) : (
                <BsCameraVideoOffFill className="text-xl sm:text-2xl md:text-3xl" />
              )}
            </Button>
          </div>
          <div className="!w-auto shadow-2xl bg-frosted-glass px-3 py-3 sm:px-5 sm:py-5 rounded-full cursor-pointer items-start self-start speaker-wrapper hidden sm:block invisible select-none">
            <GiSpeaker className="cursor-pointer text-xl sm:text-2xl md:text-3xl m-auto speaker-button" />
          </div>
        </div>
      </div>
    </>
  );
};

const Avatar = ({ avatar, className }) => {
  const avatarRef = useRef();

  useEffect(() => {
    if (avatarRef.current && avatar) {
      avatarRef.current.innerHTML = avatar;
    }
  }, [avatarRef.current, avatar]);

  return (
    <span ref={avatarRef} className={className}>
      <QuestionMarkSVG />
    </span>
  );
};

const ActiveCall = ({
  myStream,
  myAvatar,
  myMediaTrack,
  attendeeMediaTrack,
  attendeeStream,
  attendeeName,
  attendeeAvatar,
  completeCall,
  updateCallMediaTrack,
}) => {
  const myStreamRef = useRef();
  const myAvatarRef = useRef();
  const attendeeAvatarRef = useRef();
  const attendeeStreamRef = useRef();
  const activeCallWrapperRef = useRef();

  const videoStreamRef1 = useRef();
  const videoStreamRef2 = useRef();

  const [isVideoSwitched, setIsVideoSwitched] = useState(false);

  useEffect(() => {
    if (
      videoStreamRef1.current &&
      videoStreamRef2.current &&
      myStream &&
      attendeeStream
    ) {
      if (isVideoSwitched) {
        videoStreamRef1.current.srcObject = myStream;
        videoStreamRef2.current.srcObject = attendeeStream;
      } else if (!isVideoSwitched) {
        videoStreamRef1.current.srcObject = attendeeStream;
        videoStreamRef2.current.srcObject = myStream;
      }
    }
  }, [
    isVideoSwitched,
    myStream,
    attendeeStream,
    videoStreamRef1.current,
    videoStreamRef2.current,
  ]);

  useEffect(() => {
    if (myStreamRef.current && myStream) {
      myStreamRef.current.srcObject = myStream;
    }
  }, [myStreamRef.current, myStream]);

  useEffect(() => {
    if (myAvatarRef.current && myAvatar) {
      myAvatarRef.current.innerHTML = myAvatar;
    }
  }, [myAvatarRef.current, myAvatar]);

  useEffect(() => {
    if (attendeeStreamRef.current && attendeeStream) {
      attendeeStreamRef.current.srcObject = attendeeStream;
    }
  }, [attendeeStreamRef.current, attendeeStream]);

  useEffect(() => {
    if (attendeeAvatarRef.current && attendeeAvatar) {
      attendeeAvatarRef.current.innerHTML = attendeeAvatar;
    }
  }, [attendeeAvatarRef.current, attendeeAvatar]);

  return (
    <div
      className="w-full h-full flex flex-col justify-center items-center fixed bg-gray-900 inset-0 z-10"
      ref={activeCallWrapperRef}
    >
      <div className="w-full h-full relative group">
        <Video
          key={Math.random() * 956465325146851}
          className="h-full w-full object-cover inline-block"
          ref={videoStreamRef1}
          autoPlay={true}
          playsInline={true}
        />
        {!(!isVideoSwitched
          ? attendeeMediaTrack?.videoEnabled
          : myMediaTrack?.videoEnabled) && (
            <div className="h-full w-full z-10 bg-gray-800 top-0 absolute flex flex-col justify-center items-center gap-8">
              <Avatar
                avatar={!isVideoSwitched ? attendeeAvatar : myAvatar}
                className="avatarIncommingCall p-5 sm:p-10 rounded-full mb-4"
              />
            </div>
          )}
        {!attendeeMediaTrack?.audioEnabled && (
          <div className="inline-block absolute z-30 m-auto bottom-16 shadow-2xl bg-frosted-glass px-3 py-1 sm:px-3 sm:py-1 rounded-full -top-1/4 left-2/4 transform -translate-x-1/2 -translate-y-1/2">
            {attendeeName} is Muted
          </div>
        )}
        <Video
          key={Math.random() * 956465325146851}
          className="w-3/12 h-3/12 object-cover inline-block absolute z-30 m-auto right-4 bottom-4 cursor-pointer group-hover:bottom-28 sm:group-hover:bottom-36 in-call-secondary-video"
          ref={videoStreamRef2}
          autoPlay={true}
          playsInline={true}
          muted={true}
          onClick={() => setIsVideoSwitched((x) => !x)}
        />
        {!(!isVideoSwitched
          ? myMediaTrack?.videoEnabled
          : attendeeMediaTrack?.videoEnabled) && (
            <div
              className="w-3/12 h-3/12 bg-gray-800 border-4 border-gray-300 inline-block absolute z-50 m-auto right-4 bottom-4 cursor-pointer group-hover:bottom-28 sm:group-hover:bottom-36 in-call-secondary-video flex justify-center items-center"
              onClick={() => setIsVideoSwitched((x) => !x)}
            >
              <Avatar
                avatar={!isVideoSwitched ? myAvatar : attendeeAvatar}
                className="svgDimensions p-2 sm:p-1 rounded-full inline-block m-auto"
              />
            </div>
          )}
        <ActiveCallOptions
          myStream={myStream}
          attendeeStreamRef={attendeeStreamRef}
          attendeeAvatar={attendeeAvatar}
          completeCall={completeCall}
          activeCallWrapperRef={activeCallWrapperRef}
          updateCallMediaTrack={updateCallMediaTrack}
        />
      </div>
    </div>
  );
};

const VideoSettings = ({
  myStream,
  onClose,
  stopStreamTracks,
  updateMediaStream }) => {
  // https://github.com/webrtc/samples/blob/gh-pages/src/content/devices/input-output/index.html
  const audioSelectRef = useRef();
  const videoSelectRef = useRef();
  const [activeStream, setActiveStream] = useState(null);

  const getMediaDevices = (devicesInfo) => {
    const selectors = [audioSelectRef.current, videoSelectRef.current]

    // remove all options    
    selectors.forEach(select => {
      while (select.firstChild) {
        select.removeChild(select.firstChild);
      }
    });

    // TODO: add audio output source
    for (let i = 0; i !== devicesInfo?.length; ++i) {
      const deviceInfo = devicesInfo?.[i];
      const option = document.createElement('option');
      option.value = deviceInfo.deviceId;
      if (deviceInfo.kind === 'audioinput') {
        option.text = deviceInfo.label || `microphone ${audioSelectRef?.current?.length + 1}`;
        audioSelectRef?.current?.appendChild(option);
      } else if (deviceInfo.kind === 'audiooutput') {
        // option.text = deviceInfo.label || `speaker ${audioOutputSelect.length + 1}`;
        // audioOutputSelect.appendChild(option);
      }
      else if (deviceInfo.kind === 'videoinput') {
        option.text = deviceInfo.label || `camera ${videoSelectRef?.current?.length + 1}`;
        videoSelectRef?.current?.appendChild(option);
      } else {
        console.log('Some other kind of source/device: ', deviceInfo);
      }
    }


    // select the current selected devices in the UI
    if (activeStream) {
      const activeValues = activeStream?.map(x => x.id);
      Array.prototype.slice.call(audioSelectRef.current?.childNodes)?.map(x => {
        if (activeValues?.includes(x.value)) audioSelectRef.current.value = x.value
        return null;
      })
      Array.prototype.slice.call(videoSelectRef.current?.childNodes)?.map(x => {
        if (activeValues?.includes(x.value)) videoSelectRef.current.value = x.value
        return null;
      })
    }

  }

  const handleMediaDeviceError = (e) => {
    console.log("handleMediaDeviceError => ", e)
  }

  const getActiveStream = async () => {
    const _tempStream = []

    await myStream?.getTracks()?.forEach(track => {
      if (['audio', 'video']?.includes(track.kind)) {
        _tempStream?.push({
          kind: track?.kind,
          id: track?.getCapabilities()?.deviceId
        })
      }
    })

    setActiveStream(_tempStream)
  }

  const captureMedia = async () => {
    // stop current media stream track
    stopStreamTracks && await stopStreamTracks();

    // capture media with new settings
    const audioSource = audioSelectRef?.current?.value;
    const videoSource = videoSelectRef?.current?.value;
    const constraints = {
      audio: { deviceId: audioSource ? { exact: audioSource } : undefined },
      video: { deviceId: videoSource ? { exact: videoSource } : undefined }
    };

    await navigator.mediaDevices.getUserMedia(constraints).then(async stream => {
      await updateMediaStream(stream);
    });
  }

  useEffect(() => {
    if (!activeStream) getActiveStream();

    if (audioSelectRef.current && videoSelectRef.current) {
      navigator.mediaDevices.enumerateDevices().then(getMediaDevices).catch(handleMediaDeviceError);
    }
  }, [audioSelectRef.current, videoSelectRef.current])

  return <div className="z-40 w-full h-screen bg-[rgba(0,0,0,0.5)] flex items-center fixed inset-0" style={{ background: 'rgba(0,0,0,0.5)' }} onClick={(e) => e.target === e.currentTarget && onClose && onClose()}>

    <dialog open className="w-full sm:w-4/5 p-6 sm:p-8 text-white rounded-md m-auto bg-gray-900 border-2 border-white bg-gray-700" style={{ boxShadow: '0 0 10px rgba(255,255,255,0.5)', maxWidth: '810px' }}>

      <div className="flex items-center justify-between text-xl sm:text-2xl">
        <h1 className="leading-10">Camera &amp; Sound Settings</h1>
        <button className="cursor-pointer text-white" onClick={onClose}>
          <VscChromeClose
          />
        </button>
      </div>

      <div className="w-full h-0.5 bg-white rounded-sm"></div>

      <div className="w-full flex flex-col gap-2 sm:gap-4 pt-6 sm:pt-8">

        <div className="w-11/12 sm:w-4/5 m-auto grid grid-rows-2 sm:grid-rows-1 gap-0.5 sm:grid-cols-2 sm:gap-4 items-center">
          <label className="text-lg">Pick a camera</label>
          <select onChange={captureMedia} className="bg-gray-100 text-black px-2 py-2 sm:px-3 sm:py-3 rounded-sm" ref={videoSelectRef}>
            <option value={""}>Select Camera</option>
          </select>
        </div>

        <div className="w-11/12 sm:w-4/5 m-auto grid grid-rows-2 sm:grid-rows-1 gap-0.5 sm:grid-cols-2 sm:gap-4 items-center">
          <label className="text-lg">Pick a microphone</label>
          <select onChange={captureMedia} className="bg-gray-100 text-black px-2 py-2 sm:px-3 sm:py-3 rounded-sm" ref={audioSelectRef}>
            <option value={""}>Select Microphone</option>
          </select>
        </div>

        <button onClick={onClose} className="mt-8 m-auto bg-green-700 !w-fit rounded-md py-1 sm:py-2 px-3 sm:px-5 text-base sm:text-lg text-white font-medium">
          Done
        </button>

      </div>
    </dialog>
  </div>
}

const ChatDashboard = ({ myStream, socketId, getUser, makeOutgoingCall, stopStreamTracks, updateMediaStream }) => {
  const buttons = ["Profile", "My Video", "Call"];
  const [activeButtonIndex, setActiveButtonIndex] = useState(1);

  const myStreamRef1 = useRef();
  const myStreamRef2 = useRef();

  const [changeVideoSetting, setChangeVideoSettings] = useState(false)

  useEffect(() => {
    if (myStreamRef1.current && myStream && activeButtonIndex === 1) {
      myStreamRef1.current.srcObject = myStream;
    }
  }, [myStreamRef1.current, myStream, activeButtonIndex]);

  useEffect(() => {
    if (myStreamRef2.current && myStream) {
      myStreamRef2.current.srcObject = myStream;
    }
  }, [myStreamRef2.current, myStream]);

  return (
    <>
      {/* Normal Screen */}
      <div className="w-full inline-block screenVC:hidden">
        <div className="flex flex-row mb-4 gap-4 px-4">
          {buttons?.map((x, index) => (
            <Button
              className={
                index !== activeButtonIndex
                  ? `bg-transparent border-2 border-green-500 text-green-500`
                  : `bg-green-700 py-1.5`
              }
              onClick={() => setActiveButtonIndex(index)}
            >
              {x}
            </Button>
          ))}
        </div>
        <div className="sm:mt-8">
          {activeButtonIndex === 0 && (
            <div className="mt-6 w-full px-3 sm:px-24 flex justify-center">
              <Profile userMeetingId={socketId} onProfileChange={getUser} />
            </div>
          )}
          {activeButtonIndex === 1 && (
            <div className="relative">
              <Video
                key={Math.random() * 956465325146851}
                className="w-full inline-block main-video"
                ref={myStreamRef1}
                autoPlay={true}
                playsInline={true}
                muted={true}
              />
              <button
                onClick={() => setChangeVideoSettings(true)}
                className="z-10 text-lg sm:text-xl cursor-pointer p-2 border-2 rounded-full border-gray-500 bg-gray-700 hover:bg-gray-800 absolute top-4 right-4">
                <IoMdSettings
                  className="text-gray-400"
                />
              </button>
            </div>
          )}
          {activeButtonIndex === 2 && (
            <div className="w-full px-3 sm:px-24 flex justify-center">
              <MakeCall
                blockedIds={[socketId]}
                onMakeCall={(meetingId) => makeOutgoingCall(meetingId)}
              />
            </div>
          )}
        </div>
      </div>
      {/* Wide Screen */}
      <div className="w-full hidden screenVC:w-4/6 screenVC:inline-block relative">
        <button
          onClick={() => setChangeVideoSettings(true)}
          className="z-10 cursor-pointer p-2 border-2 rounded-full border-gray-500 bg-gray-700 hover:bg-gray-800 absolute top-4 right-4">
          <IoMdSettings
            size="24"
            className="text-gray-400"
          />
        </button>
        <Video
          className="md:rounded-2xl main-video"
          ref={myStreamRef2}
          autoPlay={true}
          playsInline={true}
          muted={true}
        />
      </div>
      <div className="w-full hidden screenVC:w-2/6 screenVC:inline-block mt-0 pl-4">
        <Profile userMeetingId={socketId} onProfileChange={getUser} />
        <MakeCall
          blockedIds={[socketId]}
          onMakeCall={(meetingId) => makeOutgoingCall(meetingId)}
        />
      </div>
      {changeVideoSetting && <VideoSettings myStream={myStream} updateMediaStream={updateMediaStream} stopStreamTracks={stopStreamTracks} onClose={() => setChangeVideoSettings(false)} />}
    </>
  );
};

const VideoChat = () => {
  const {
    currentCall,
    myStream,
    mySocketId,
    triggerGetUser,
    triggerStopStreamTracks,
    triggerUpdateMediaStream,
    triggerOutgoingCall,
    triggerCancelOutgoingCall,
    triggerAcceptIncommingCall,
    triggerUnsetCurrentCall,
    triggerDeclineIncommingCall,
    triggerCompleteCall,
    triggerUpdateCallMediaTrack,
  } = useContext(VideoChatContext);

  useEffect(() => {
    if (mySocketId && myStream && queryParamsExists("call")) {
      const callerId = getQueryParamValue("call");
      removeFromURL("call");
      setTimeout(function () {
        triggerOutgoingCall(callerId);
      }, 3000);
    }
  }, [mySocketId, myStream]);

  return (
    <>
      {currentCall?.type === callTypes.OUTGOING &&
        currentCall?.status !== callStatus.ACCEPTED && (
          <Calling
            callerId={currentCall?.attendee?.id}
            callerName={currentCall?.attendee?.name}
            callerAvatar={currentCall?.attendee?.avatar}
            isCallerOffline={
              currentCall?.attendee?.status === userStatus.OFFLINE
            }
            isCallerNotFound={
              currentCall?.attendee?.status === userStatus.NOT_FOUND
            }
            isRinging={currentCall?.status === callStatus.RINGING}
            onHangup={triggerCancelOutgoingCall}
            onContinue={triggerUnsetCurrentCall}
            isDeclined={currentCall?.status === callStatus.DECLINED}
            isCompleted={currentCall?.status === callStatus.COMPLETED}
            duration={currentCall?.duration}
            isNotAnswering={currentCall?.status === callStatus.NOT_ANSWERED}
            isMissed={currentCall?.status === callStatus.CANCELED}
          />
        )}
      {currentCall?.type === callTypes.INCOMMING &&
        currentCall?.status !== callStatus.ACCEPTED && (
          <IncommingCall
            incommingCallerId={currentCall?.from?.id}
            incommingCallerName={currentCall?.from?.name}
            incommingCallerAvatar={currentCall?.from?.avatar}
            isIncommingCallerOffline={
              currentCall?.from?.status === userStatus.OFFLINE
            }
            onAccept={triggerAcceptIncommingCall}
            isMissed={currentCall?.status === callStatus?.CANCELED}
            onContinue={triggerUnsetCurrentCall}
            isCompleted={currentCall?.status === callStatus.COMPLETED}
            onDecline={triggerDeclineIncommingCall}
            duration={currentCall?.duration}
          />
        )}
      {currentCall?.status === callStatus.ACCEPTED && (
        <ActiveCall
          myStream={myStream}
          myAvatar={
            currentCall?.from?.id === mySocketId
              ? currentCall?.from?.avatar
              : currentCall?.attendee?.avatar
          }
          myMediaTrack={
            currentCall?.from?.id === mySocketId
              ? currentCall?.from?.mediaTrack
              : currentCall?.attendee?.mediaTrack
          }
          attendeeMediaTrack={
            currentCall?.from?.id !== mySocketId
              ? currentCall?.from?.mediaTrack
              : currentCall?.attendee?.mediaTrack
          }
          attendeeStream={
            currentCall?.from?.id !== mySocketId
              ? currentCall?.from?.stream
              : currentCall?.attendee?.stream
          }
          attendeeName={
            currentCall?.from?.id !== mySocketId
              ? currentCall?.from?.name
              : currentCall?.attendee?.name
          }
          attendeeAvatar={
            currentCall?.from?.id !== mySocketId
              ? currentCall?.from?.avatar
              : currentCall?.attendee?.avatar
          }
          completeCall={triggerCompleteCall}
          updateCallMediaTrack={triggerUpdateCallMediaTrack}
        />
      )}
      {currentCall?.status !== callStatus.ACCEPTED && (
        <ChatDashboard
          myStream={myStream}
          socketId={mySocketId}
          getUser={triggerGetUser}
          makeOutgoingCall={triggerOutgoingCall}
          stopStreamTracks={triggerStopStreamTracks}
          updateMediaStream={triggerUpdateMediaStream}
        />
      )}
    </>
  );
};

const VideoChatContextWrapper = () => {
  return (
    <ContextProvider>
      <VideoChat />
    </ContextProvider>
  );
};

export default VideoChatContextWrapper;
